/**
 * Admin Dashboard
 *
 * Overview page showing pending users, org stats, and quick actions
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import { getPendingUsers, getAllOrganizations } from '../../services/firestore';
import { clearCache, getCacheStats } from '../../services/institutionService';
import type { UserProfile, Organization } from '../../services/firestore';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminDashboard.css';

interface CacheStats {
  institutions: number;
  ratings: number;
  indicators: number;
}

interface DashboardStats {
  pendingUsers: number;
  totalOrganizations: number;
  totalCRAs: number;
  totalBanks: number;
}

export const AdminDashboard: React.FC = () => {
  const { isPlatformAdmin } = useAuth();
  const { effectiveIsPlatformAdmin, effectiveIsCRAAdmin, effectiveOrganization } = useRoleAssumption();
  const { t } = useTranslation();
  const [pendingUsers, setPendingUsers] = useState<UserProfile[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    pendingUsers: 0,
    totalOrganizations: 0,
    totalCRAs: 0,
    totalBanks: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cache management state (Platform Admin only)
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const [isClearingCache, setIsClearingCache] = useState(false);
  const [cacheMessage, setCacheMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCacheConfirm, setShowCacheConfirm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Only actual platform admins can fetch pending users and all organizations
        // (Firestore rules require actual role, not assumed)
        // But we use effectiveIsPlatformAdmin to decide what UI to show
        if (isPlatformAdmin && effectiveIsPlatformAdmin) {
          const [users, orgs] = await Promise.all([
            getPendingUsers(),
            getAllOrganizations(),
          ]);

          setPendingUsers(users);
          setOrganizations(orgs);

          setStats({
            pendingUsers: users.length,
            totalOrganizations: orgs.length,
            totalCRAs: orgs.filter(o => o.type === 'cra').length,
            totalBanks: orgs.filter(o => o.type === 'bank').length,
          });
        } else {
          // CRA/Bank admins (or platform admin viewing as other role)
          // don't have access to pending users list
          // They see a simpler dashboard with their org info
          setPendingUsers([]);
          setOrganizations([]);
          setStats({
            pendingUsers: 0,
            totalOrganizations: 0,
            totalCRAs: 0,
            totalBanks: 0,
          });
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(t('adminDashboard.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isPlatformAdmin, effectiveIsPlatformAdmin]);

  // Fetch cache stats for platform admins
  useEffect(() => {
    const fetchCacheStats = async () => {
      if (isPlatformAdmin && effectiveIsPlatformAdmin) {
        try {
          const stats = await getCacheStats();
          setCacheStats(stats);
        } catch (err) {
          console.error('Error fetching cache stats:', err);
        }
      }
    };
    fetchCacheStats();
  }, [isPlatformAdmin, effectiveIsPlatformAdmin]);

  // Handle cache clear
  const handleClearCache = async () => {
    setIsClearingCache(true);
    setCacheMessage(null);
    try {
      const cleared = await clearCache();
      setCacheMessage({
        type: 'success',
        text: `Cache cleared: ${cleared.institutions} institutions, ${cleared.ratings} ratings, ${cleared.indicators} indicators`,
      });
      // Update stats to show zero
      setCacheStats({ institutions: 0, ratings: 0, indicators: 0 });
    } catch (err) {
      console.error('Error clearing cache:', err);
      setCacheMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to clear cache',
      });
    } finally {
      setIsClearingCache(false);
      setShowCacheConfirm(false);
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => setCacheMessage(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <h1>{t('adminDashboard.title')}</h1>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <h1>{t('adminDashboard.title')}</h1>
        <div className="admin-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <h1>{t('adminDashboard.title')}</h1>

      {/* Stats Cards */}
      <div className="stats-grid">
        {/* Platform Admin Stats */}
        {effectiveIsPlatformAdmin && (
          <>
            <div className="stat-card pending">
              <div className="stat-icon">⏳</div>
              <div className="stat-content">
                <h3>{stats.pendingUsers}</h3>
                <p>{t('adminDashboard.pendingApprovals')}</p>
              </div>
              <Link to="/admin/users/pending" className="stat-link">
                {t('common.viewAll')} →
              </Link>
            </div>

            <div className="stat-card orgs">
              <div className="stat-icon">🏢</div>
              <div className="stat-content">
                <h3>{stats.totalOrganizations}</h3>
                <p>{t('adminDashboard.totalOrganizations')}</p>
              </div>
              <Link to="/admin/organizations" className="stat-link">
                {t('common.viewAll')} →
              </Link>
            </div>

            <div className="stat-card cras">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>{stats.totalCRAs}</h3>
                <p>{t('adminDashboard.creditRatingAgencies')}</p>
              </div>
            </div>

            <div className="stat-card banks">
              <div className="stat-icon">🏦</div>
              <div className="stat-content">
                <h3>{stats.totalBanks}</h3>
                <p>{t('adminDashboard.clientBanks')}</p>
              </div>
            </div>
          </>
        )}

        {/* CRA Admin Stats */}
        {effectiveIsCRAAdmin && effectiveOrganization && (
          <>
            <div className="stat-card org">
              <div className="stat-icon">🏢</div>
              <div className="stat-content">
                <h3>{effectiveOrganization.name}</h3>
                <p>{t('adminDashboard.yourOrganization')}</p>
              </div>
            </div>

            <div className="stat-card licenses">
              <div className="stat-icon">📜</div>
              <div className="stat-content">
                <h3>{effectiveOrganization.licenses?.analyst_seats_used || 0} / {effectiveOrganization.licenses?.analyst_seats || 0}</h3>
                <p>{t('adminDashboard.analystSeatsUsed')}</p>
              </div>
              <Link to="/admin/licenses" className="stat-link">
                {t('adminDashboard.viewLicenses')} →
              </Link>
            </div>

            <div className="stat-card team">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>{t('adminDashboard.team')}</h3>
                <p>{t('adminDashboard.manageTeamMembers')}</p>
              </div>
              <Link to="/admin/users" className="stat-link">
                {t('adminDashboard.viewTeam')} →
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Recent Pending Users */}
      {pendingUsers.length > 0 && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>{t('adminDashboard.recentPendingApprovals')}</h2>
            <Link to="/admin/users/pending" className="section-link">
              {t('common.viewAll')} ({pendingUsers.length})
            </Link>
          </div>
          <div className="pending-list">
            {pendingUsers.slice(0, 5).map(user => (
              <div key={user.uid} className="pending-item">
                <div className="pending-info">
                  <span className="pending-email">{user.email}</span>
                  <span className="pending-provider">{user.auth_provider}</span>
                  <span className="pending-date">
                    {user.created_at?.toDate
                      ? user.created_at.toDate().toLocaleDateString()
                      : t('adminDashboard.unknownDate')}
                  </span>
                </div>
                <Link to="/admin/users/pending" className="pending-action">
                  {t('common.review')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="dashboard-section">
        <h2>{t('adminDashboard.quickActions')}</h2>
        <div className="quick-actions">
          {/* Platform Admin Actions */}
          {effectiveIsPlatformAdmin && (
            <>
              <Link to="/admin/users/pending" className="quick-action-btn">
                <span className="action-icon">✅</span>
                <span>{t('adminDashboard.approveUsers')}</span>
              </Link>
              <Link to="/admin/organizations/create" className="quick-action-btn">
                <span className="action-icon">➕</span>
                <span>{t('adminDashboard.createOrganization')}</span>
              </Link>
              <Link to="/admin/users" className="quick-action-btn">
                <span className="action-icon">👥</span>
                <span>{t('adminDashboard.manageUsers')}</span>
              </Link>
            </>
          )}

          {/* CRA Admin Actions */}
          {effectiveIsCRAAdmin && (
            <>
              <Link to="/admin/users" className="quick-action-btn">
                <span className="action-icon">👥</span>
                <span>{t('adminDashboard.manageTeam')}</span>
              </Link>
              <Link to="/admin/licenses" className="quick-action-btn">
                <span className="action-icon">📜</span>
                <span>{t('adminDashboard.viewLicenses')}</span>
              </Link>
            </>
          )}

          <Link to="/" className="quick-action-btn">
            <span className="action-icon">📈</span>
            <span>{t('adminDashboard.goToApp')}</span>
          </Link>
        </div>
      </div>

      {/* Recent Organizations (Platform Admin only) */}
      {effectiveIsPlatformAdmin && organizations.length > 0 && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>{t('adminDashboard.recentOrganizations')}</h2>
            <Link to="/admin/organizations" className="section-link">
              {t('common.viewAll')}
            </Link>
          </div>
          <div className="org-list">
            {organizations.slice(0, 5).map(org => (
              <div key={org.id} className="org-item">
                <div className="org-info">
                  <span className="org-name">{org.name}</span>
                  <span className={`org-type ${org.type}`}>{org.type.toUpperCase()}</span>
                  <span className={`org-status ${org.status}`}>{org.status}</span>
                </div>
                <span className="org-institutions">
                  {t('adminDashboard.institutionsCount', { count: org.institutions_access?.length || 0 })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* System Administration (Platform Admin only) */}
      {effectiveIsPlatformAdmin && (
        <div className="dashboard-section system-admin-section">
          <h2>{t('adminDashboard.systemAdministration')}</h2>
          <p className="section-description">
            {t('adminDashboard.cacheDescription')}
          </p>

          {/* Cache Stats */}
          {cacheStats && (
            <div className="cache-stats">
              <div className="cache-stat-item">
                <span className="cache-stat-label">{t('adminDashboard.cacheInstitutions')}</span>
                <span className="cache-stat-value">{cacheStats.institutions}</span>
              </div>
              <div className="cache-stat-item">
                <span className="cache-stat-label">{t('adminDashboard.cacheRatings')}</span>
                <span className="cache-stat-value">{cacheStats.ratings}</span>
              </div>
              <div className="cache-stat-item">
                <span className="cache-stat-label">{t('adminDashboard.cacheIndicators')}</span>
                <span className="cache-stat-value">{cacheStats.indicators}</span>
              </div>
            </div>
          )}

          {/* Cache Message */}
          {cacheMessage && (
            <div className={`cache-message ${cacheMessage.type}`}>
              {cacheMessage.type === 'success' ? '✓' : '✗'} {cacheMessage.text}
            </div>
          )}

          {/* Clear Cache Button / Confirmation */}
          {!showCacheConfirm ? (
            <button
              className="clear-cache-btn"
              onClick={() => setShowCacheConfirm(true)}
              disabled={isClearingCache}
            >
              <span className="action-icon">🗑️</span>
              {t('adminDashboard.clearDataCache')}
            </button>
          ) : (
            <div className="cache-confirm">
              <p>{t('adminDashboard.cacheConfirm')}</p>
              <div className="cache-confirm-actions">
                <button
                  className="confirm-btn cancel"
                  onClick={() => setShowCacheConfirm(false)}
                  disabled={isClearingCache}
                >
                  {t('adminDashboard.cancel')}
                </button>
                <button
                  className="confirm-btn confirm"
                  onClick={handleClearCache}
                  disabled={isClearingCache}
                >
                  {isClearingCache ? t('adminDashboard.clearing') : t('adminDashboard.yesClearCache')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
